import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import GoldSeam from "@/components/GoldSeam";
import { waLink } from "@/lib/site";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 90]);
  const bgY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 140]);

  return (
    <section className="relative bg-steel overflow-hidden" data-testid="hero-section">
      <motion.div
        className="absolute inset-0 -bottom-32 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/metal-hero.jpg')", y: bgY }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(102deg, rgba(20,22,24,0.92) 0%, rgba(35,38,42,0.78) 44%, rgba(66,70,75,0.5) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute -top-40 -left-32 w-[620px] h-[620px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,190,138,0.12), transparent 64%)" }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 pt-36 pb-24 md:pt-44 md:pb-32 grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <h1
            className="font-display text-offwhite font-medium leading-[1.06]"
            style={{ fontSize: "clamp(2.3rem, 4.4vw, 3.5rem)", textShadow: "0 1px 30px rgba(0,0,0,0.35)" }}
            data-testid="hero-headline"
          >
            <Line delay={0.3}>You don't erase the pattern.</Line>
            <Line delay={0.46}>
              You trace it in <em className="not-italic text-goldsoft">gold</em>
            </Line>
            <Line delay={0.62}>until it becomes wisdom.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-[#D5D1C9] text-base md:text-lg max-w-xl leading-relaxed"
            data-testid="hero-subline"
          >
            Human dynamics, emotional intelligence and transformation work with
            Rajdeep Brar — counselling for individuals, and for the organisations they run.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-11 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/booking"
              data-testid="hero-book-session-button"
              className="bg-goldsoft text-steel text-xs uppercase tracking-[0.24em] font-semibold px-9 py-4 hover:bg-offwhite transition-colors duration-300"
            >
              Book a Session
            </Link>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-link"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#D5D1C9] hover:text-goldsoft transition-colors duration-300"
            >
              <MessageCircle size={14} strokeWidth={1.6} /> Or ask on WhatsApp
            </a>
          </motion.div>

          <GoldSeam variant="horizontal" className="mt-14 w-64 h-10 opacity-80" delay={1.2} />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          style={{ y: imgY }}
          className="lg:col-span-5 relative"
        >
          <div className="relative border border-white/20 bg-white/5 backdrop-blur-sm p-3">
            <span className="absolute -top-px -left-px w-5 h-5 border-t border-l border-goldsoft" aria-hidden="true" />
            <span className="absolute -bottom-px -right-px w-5 h-5 border-b border-r border-goldsoft" aria-hidden="true" />
            <img
              src="/images/hero-still.jpg"
              alt="A pale ceramic bowl mended with fine gold seams, resting on cream linen in morning light"
              className="w-full h-[300px] md:h-[420px] lg:h-[520px] object-cover"
              data-testid="hero-image"
            />
          </div>
          <p className="mt-4 text-[10px] uppercase tracking-[0.28em] text-[#B9B4AA]">
            Kintsugi — the repair, left visible
          </p>
        </motion.div>
      </div>
    </section>
  );
}
